// src/pages/Tasks/TaskCreate.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { tasksApi, TaskCreatePayload, TaskType } from '../../api/tasks';
import * as chatApi from '../../api/chat';
import { getAllUsers } from '../../api/user';

interface TaskCreateContext {
  conversationId?: string | null;
  messageId?: string | null;
  initialTitle?: string;
  initialDescription?: string;
  availableUserIds?: string[];
}

interface TaskCreateProps {
  isOpen?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
  onCreated?: (taskId: string) => void;
  chatContext?: TaskCreateContext;
}

interface User {
  id: string;
  user_id?: string;
  name: string;
  email: string;
  role?: string;
  department?: string;
  department_id?: string;
  employee_id?: string;
}

interface ApprovalLevel {
  level: number;
  approver: string;
  roleLabel: string;
  status: string;
  actionAt: string | null;
  remark: string;
}

interface PreparorAssignment {
  user_id: string;
  role_in_task: 'PREPAROR';
}

function buildInitialForm(chatContext?: TaskCreateContext): TaskCreatePayload & { approvalChain?: ApprovalLevel[] } {
  return {
    title: chatContext?.initialTitle || '',
    description: chatContext?.initialDescription || '',
    task_type: 'GENERAL',
    priority: 'MEDIUM',
    department_id: '',
    due_date: '',
    estimated_hours: undefined,
    assignees: [],
    checklist: [''],
    approvalChain: [
      {
        level: 1,
        approver: '',
        roleLabel: 'Reviewer',
        status: 'Pending',
        actionAt: null,
        remark: '',
      },
    ],
  };
}

function TaskCreate({ isOpen = true, onClose, onSuccess, onCreated, chatContext }: TaskCreateProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState<'basic' | 'details' | 'approval' | 'checklist'>('basic');
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [loadingTaskTypes, setLoadingTaskTypes] = useState(false);
  const [showCreateTaskTypeModal, setShowCreateTaskTypeModal] = useState(false);
  const [newTaskTypeName, setNewTaskTypeName] = useState('');
  const [creatingTaskType, setCreatingTaskType] = useState(false);
  
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  const [selectedPreparors, setSelectedPreparors] = useState<string[]>([]);
  
  const [formData, setFormData] = useState<TaskCreatePayload & { approvalChain?: ApprovalLevel[] }>(
    buildInitialForm(chatContext)
  );

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await getAllUsers();
      const usersData = response?.users || [];
      setUsers(usersData);
      console.log('Users fetched:', usersData.length);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchTaskTypes();
      fetchUsers();
    }
  }, [isOpen]);

  const fetchTaskTypes = async () => {
    setLoadingTaskTypes(true);
    try {
      const response = await tasksApi.getTaskTypes();
      const taskTypesData = Array.isArray(response.data?.items) ? response.data.items : [];
      setTaskTypes(taskTypesData);
    } catch (error) {
      console.error('Failed to fetch task types:', error);
      setTaskTypes([]);
    } finally {
      setLoadingTaskTypes(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      setActiveSection('basic');
    }
  }, [chatContext, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setFormData(buildInitialForm(chatContext));
      setSelectedPreparors([]);
      setActiveSection('basic');
    }
  }, [chatContext, isOpen]);

  const resetForm = () => {
    setFormData(buildInitialForm(chatContext));
    setSelectedPreparors([]);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleTaskTypeChange = (value: string) => {
    if (value === '__CREATE_NEW__') {
      setShowCreateTaskTypeModal(true);
    } else {
      setFormData({ ...formData, task_type: value });
    }
  };

  const addPreparor = () => {
    setSelectedPreparors([...selectedPreparors, '']);
  };

  const removePreparor = (index: number) => {
    setSelectedPreparors(selectedPreparors.filter((_, i) => i !== index));
  };

  const updatePreparor = (index: number, userId: string) => {
    const newPreparors = [...selectedPreparors];
    newPreparors[index] = userId;
    setSelectedPreparors(newPreparors);
  };

  const addApprovalLevel = () => {
    setFormData((prev) => ({
      ...prev,
      approvalChain: [
        ...(prev.approvalChain || []),
        {
          level: (prev.approvalChain?.length || 0) + 1,
          approver: '',
          roleLabel: `Level ${(prev.approvalChain?.length || 0) + 1} Approver`,
          status: 'Pending',
          actionAt: null,
          remark: '',
        },
      ],
    }));
  };

  const removeApprovalLevel = (index: number) => {
    if ((formData.approvalChain?.length || 0) <= 1) return;
    setFormData((prev) => ({
      ...prev,
      approvalChain: (prev.approvalChain || []).filter((_, i) => i !== index),
    }));
  };

  const updateApprovalLevel = (index: number, field: keyof ApprovalLevel, value: string) => {
    setFormData((prev) => ({
      ...prev,
      approvalChain: (prev.approvalChain || []).map((level, i) =>
        i === index ? { ...level, [field]: value } : level
      ),
    }));
  };

  // FIXED: Checklist handlers with proper state management
  const handleChecklistChange = useCallback((index: number, value: string) => {
    setFormData(prev => {
      const newChecklist = [...(prev.checklist || [])];
      newChecklist[index] = value;
      return { ...prev, checklist: newChecklist };
    });
  }, []);

  const addChecklistItem = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      checklist: [...(prev.checklist || []), ''],
    }));
  }, []);

  const removeChecklistItem = useCallback((index: number) => {
    setFormData(prev => {
      const newChecklist = (prev.checklist || []).filter((_, i) => i !== index);
      // If checklist becomes empty, add one empty item
      if (newChecklist.length === 0) {
        return { ...prev, checklist: [''] };
      }
      return { ...prev, checklist: newChecklist };
    });
  }, []);

  const handleCreateTaskType = async () => {
    if (!newTaskTypeName.trim()) {
      alert('Please enter a task type name');
      return;
    }

    setCreatingTaskType(true);
    try {
      const response = await tasksApi.createTaskType({ name: newTaskTypeName });
      const newTaskType = response.data;
      
      setTaskTypes(prev => {
        const currentTypes = Array.isArray(prev) ? prev : [];
        return [...currentTypes, newTaskType];
      });
      
      setFormData({ ...formData, task_type: newTaskType.name });
      setShowCreateTaskTypeModal(false);
      setNewTaskTypeName('');
    } catch (error: any) {
      console.error('Failed to create task type:', error);
      alert(error?.response?.data?.message || error?.message || 'Failed to create task type');
    } finally {
      setCreatingTaskType(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedPreparors.length === 0 || selectedPreparors.every(p => !p)) {
      alert('Please select at least one Preparor (Worker) for this task');
      return;
    }
    
    setLoading(true);
    
    const assignees: Array<{ user_id: string; role_in_task: 'PREPAROR' | 'REVIEWER' }> = [];
    
    selectedPreparors.forEach(preparorId => {
      if (preparorId) {
        assignees.push({
          user_id: preparorId,
          role_in_task: 'PREPAROR' as const,
        });
      }
    });
    
    (formData.approvalChain || []).forEach(level => {
      if (level.approver) {
        assignees.push({
          user_id: level.approver,
          role_in_task: 'REVIEWER' as const,
        });
      }
    });
    
    const payload: TaskCreatePayload = {
      title: formData.title,
      description: formData.description || undefined,
      task_type: formData.task_type,
      priority: formData.priority,
      department_id: formData.department_id || undefined,
      due_date: formData.due_date || undefined,
      estimated_hours: formData.estimated_hours ? parseFloat(String(formData.estimated_hours)) : undefined,
      checklist: (formData.checklist || []).filter(item => item.trim()),
      assignees: assignees,
    };

    try {
      if (chatContext?.messageId) {
        const response = await chatApi.convertToTask(chatContext.messageId, payload);
        resetForm();
        onCreated?.(response.task_id);
        onSuccess?.();
        handleClose();
        return;
      }

      if (chatContext?.conversationId) {
        const response = await chatApi.createTaskForConv(chatContext.conversationId, payload);
        resetForm();
        onCreated?.(response.task_id);
        onSuccess?.();
        handleClose();
        return;
      }

      const response = await tasksApi.create(payload);
      resetForm();
      onCreated?.(response.data.task.id);
      if (onClose) onClose();
      if (onSuccess) {
        onSuccess();
      } else {
        navigate(`/tasks/${response.data.task.id}`);
      }
    } catch (err: any) {
      alert(err?.response?.data?.detail || err.message || 'Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    if (onClose) {
      onClose();
      return;
    }
    navigate('/tasks/list');
  };

  const isBasicValid = formData.title.trim().length > 0;
  
  const calculateProgress = () => {
    const totalFields = 9;
    let filledFields = 0;
    
    if (formData.title) filledFields++;
    if (formData.description) filledFields++;
    if (formData.task_type) filledFields++;
    if (formData.priority) filledFields++;
    if (formData.department_id) filledFields++;
    if (formData.due_date) filledFields++;
    if (formData.estimated_hours) filledFields++;
    if (selectedPreparors.length > 0 && selectedPreparors.some(p => p)) filledFields++;
    if (formData.checklist && formData.checklist.length > 0 && formData.checklist.some(item => item.trim())) filledFields++;
    
    return (filledFields / totalFields) * 100;
  };
  
  const progress = calculateProgress();

  const getUserName = (userId: string) => {
    const user = users.find(u => u.id === userId || u.user_id === userId);
    if (user) {
      const dept = user.department || user.department_id || '';
      return `${user.name}${dept ? ` (${dept})` : ''}`;
    }
    return '-- Select --';
  };

  const visibleUsers = chatContext?.availableUserIds?.length
    ? users.filter(user => chatContext.availableUserIds?.includes(user.id || user.user_id || ''))
    : users;

  const preparorUsers = visibleUsers;
  const approverUsers = visibleUsers;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 transition-opacity duration-300"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div 
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden animate-in fade-in zoom-in duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with Progress Bar */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 px-6 py-5">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Create New Task</h2>
                  </div>
                  <p className="text-sm text-gray-500 ml-10">Fill in the details to create a new task</p>
                </div>
                <button
                  onClick={handleClose}
                  className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Progress Bar */}
              <div className="mt-4">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Form Completion</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-purple-600 h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Step Indicator */}
            <div className="px-6 pt-4 border-b border-gray-100">
              <div className="flex gap-2 overflow-x-auto">
                {['basic', 'details', 'approval', 'checklist'].map((section, idx) => (
                  <button
                    key={section}
                    onClick={() => setActiveSection(section as any)}
                    className={`
                      px-4 py-2 text-sm font-medium rounded-t-lg transition-all duration-200 whitespace-nowrap
                      ${activeSection === section
                        ? 'bg-white text-blue-600 border-b-2 border-blue-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }
                    `}
                  >
                    <span className="flex items-center gap-2">
                      <span className={`
                        w-5 h-5 rounded-full flex items-center justify-center text-xs
                        ${activeSection === section 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-200 text-gray-500'
                        }
                      `}>
                        {idx + 1}
                      </span>
                      {section === 'approval' ? 'Approval Chain' : section.charAt(0).toUpperCase() + section.slice(1)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Form Body */}
            <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Basic Information Section */}
                {activeSection === 'basic' && (
                  <div className="space-y-5 animate-in fade-in duration-300">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Task Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                        maxLength={500}
                        placeholder="e.g., Complete project documentation"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        autoFocus
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        {formData.title.length}/500 characters
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description</label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={4}
                        placeholder="Describe what needs to be done..."
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                      />
                    </div>

                    <div className="flex justify-end pt-4">
                      <button
                        type="button"
                        onClick={() => setActiveSection('details')}
                        disabled={!isBasicValid}
                        className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2 shadow-md"
                      >
                        Next: Details
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Details Section */}
                {activeSection === 'details' && (
                  <div className="space-y-5 animate-in fade-in duration-300">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Task Type</label>
                        <select 
                          value={formData.task_type} 
                          onChange={(e) => handleTaskTypeChange(e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          disabled={loadingTaskTypes}
                        >
                          {loadingTaskTypes ? (
                            <option>Loading task types...</option>
                          ) : (
                            <>
                              {/* <optgroup label="Default Types">
                                <option value="GENERAL">📋 General</option>
                                <option value="BUG">🐛 Bug</option>
                                <option value="FEATURE">✨ Feature</option>
                                <option value="SUPPORT">🆘 Support</option>
                                <option value="REVIEW">👀 Review</option>
                              </optgroup> */}
                              
                              {taskTypes && taskTypes.length > 0 && (
                                <optgroup>
                                  {taskTypes.map((type) => (
                                    <option key={type.id} value={type.name}>
                                      {type.name}
                                    </option>
                                  ))}
                                </optgroup>
                              )}
                              
                              <option disabled style={{ backgroundColor: '#f0f0f0', color: '#999' }}>──────────</option>
                              
                              <option 
                                value="__CREATE_NEW__"
                                style={{ 
                                  backgroundColor: '#f0f9ff', 
                                  color: '#3b82f6',
                                  fontWeight: 'bold'
                                }}
                              >
                                ➕ Create New Type
                              </option>
                            </>
                          )}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Priority</label>
                        <select 
                          name="priority" 
                          value={formData.priority} 
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        >
                          <option value="LOW">🟢 Low</option>
                          <option value="MEDIUM">🟡 Medium</option>
                          <option value="HIGH">🟠 High</option>
                          <option value="URGENT">🔴 Urgent</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Department Name</label>
                        <input
                          type="text"
                          name="department_id"
                          value={formData.department_id}
                          onChange={handleChange}
                          placeholder="e.g., ENG, SALES, HR"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        />
                      </div> */}

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Due Date</label>
                        <input
                          type="datetime-local"
                          name="due_date"
                          value={formData.due_date}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Estimated Hours</label>
                      <input  
                      
                        type="number"
                        name="estimated_hours"
                        value={formData.estimated_hours || ''}
                        onChange={handleChange}
                        step="0.5"
                        min="0"
                        placeholder="e.g., 4, 6.5, 8"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                    </div>

                    <div className="flex justify-between pt-4">
                      <button
                        type="button"
                        onClick={() => setActiveSection('basic')}
                        className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveSection('approval')}
                        className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium flex items-center gap-2 shadow-md"
                      >
                        Next: Approval Chain
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Approval Chain Section */}
                {activeSection === 'approval' && (
                  <div className="space-y-5 animate-in fade-in duration-300">
                    {/* Multiple Preparors Selection */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
                      <div className="flex justify-between items-center mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">1</div>
                            <h3 className="font-semibold text-gray-900">Preparors (Workers)</h3>
                            <span className="text-xs text-red-500">*</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">The people who will actually do the work</p>
                        </div>
                        <button
                          type="button"
                          onClick={addPreparor}
                          className="px-3 py-1.5 bg-blue-500 text-white text-xs rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-1"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add Preparor
                        </button>
                      </div>
                      
                      <div className="space-y-2">
                        {selectedPreparors.length === 0 ? (
                          <button
                            type="button"
                            onClick={addPreparor}
                            className="w-full py-3 border-2 border-dashed border-blue-300 rounded-lg text-blue-600 text-sm hover:border-blue-400 hover:bg-blue-50 transition-colors"
                          >
                            + Click to add first preparor
                          </button>
                        ) : (
                          selectedPreparors.map((preparorId, idx) => (
                            <div key={idx} className="flex gap-2 items-center group">
                              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-xs text-blue-600 font-medium">
                                {idx + 1}
                              </div>
                              <select
                                value={preparorId}
                                onChange={(e) => updatePreparor(idx, e.target.value)}
                                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                disabled={loadingUsers}
                              >
                                <option value="">-- Select Preparor --</option>
                                {loadingUsers ? (
                                  <option>Loading users...</option>
                                ) : (
                                  preparorUsers.map(user => (
                                    <option key={user.id || user.user_id} value={user.id || user.user_id}>
                                      {user.name} {user.role ? `(${user.role})` : ''}
                                    </option>
                                  ))
                                )}
                              </select>
                              {selectedPreparors.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removePreparor(idx)}
                                  className="opacity-0 group-hover:opacity-100 p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                      
                      {selectedPreparors.length > 0 && (
                        <div className="mt-3 text-xs text-gray-500">
                          <span className="font-medium">{selectedPreparors.filter(p => p).length}</span> preparor(s) selected
                        </div>
                      )}
                    </div>

                    {/* Approval Chain Visual Flow */}
                    <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold">2</div>
                            <h3 className="font-semibold text-gray-900">Approval Chain</h3>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Set who reviews this task in order</p>
                        </div>
                        <button
                          type="button"
                          onClick={addApprovalLevel}
                          className="px-3 py-1.5 bg-purple-500 text-white text-xs rounded-lg hover:bg-purple-600 transition-colors flex items-center gap-1"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add Level
                        </button>
                      </div>

                      {/* Visual Flow Diagram */}
                      <div className="flex items-center gap-1 mb-5 p-3 bg-white rounded-lg border border-gray-200 overflow-x-auto">
                        <div className="flex flex-col items-center flex-shrink-0">
                          <div className="w-12 h-12 rounded-full bg-blue-100 border-2 border-blue-500 flex items-center justify-center">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                          </div>
                          <span className="text-xs font-semibold text-blue-600 mt-1">PREPARORS</span>
                          <span className="text-xs text-gray-400">{selectedPreparors.filter(p => p).length} Worker(s)</span>
                        </div>

                        {(formData.approvalChain || []).map((level, idx) => (
                          <div key={idx} className="flex items-center">
                            <div className="w-8 h-0.5 bg-gray-300" />
                            <div className="flex flex-col items-center flex-shrink-0">
                              <div className="w-10 h-10 rounded-full bg-orange-100 border-2 border-orange-500 flex items-center justify-center">
                                <span className="text-sm font-bold text-orange-600">L{level.level}</span>
                              </div>
                              <span className="text-xs font-semibold text-orange-600 mt-1 max-w-[60px] text-center truncate">
                                {level.roleLabel || `Reviewer`}
                              </span>
                            </div>
                          </div>
                        ))}

                        <div className="w-8 h-0.5 bg-gray-300" />
                        <div className="flex flex-col items-center flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-green-100 border-2 border-green-500 flex items-center justify-center">
                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <span className="text-xs font-semibold text-green-600 mt-1">DONE</span>
                        </div>
                      </div>

                      {/* Approval Levels List */}
                      <div className="space-y-3">
                        {(formData.approvalChain || []).map((level, idx) => (
                          <div key={idx} className="bg-white rounded-lg p-4 border border-gray-200">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 text-xs font-bold flex items-center justify-center">
                                  {level.level}
                                </div>
                                <span className="font-medium text-gray-700">Level {level.level} Approval</span>
                              </div>
                              {(formData.approvalChain?.length || 0) > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeApprovalLevel(idx)}
                                  className="text-red-500 hover:text-red-700 text-xs px-2 py-1 rounded hover:bg-red-50"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Who Will Approve</label>
                                <select
                                  value={level.approver}
                                  onChange={(e) => updateApprovalLevel(idx, 'approver', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                                  disabled={loadingUsers}
                                >
                                  <option value="">-- Select --</option>
                                  {loadingUsers ? (
                                    <option>Loading users...</option>
                                  ) : (
                                    approverUsers.map(user => (
                                      <option key={user.id || user.user_id} value={user.id || user.user_id}>
                                        {user.name} {user.role ? `(${user.role})` : ''}
                                      </option>
                                    ))
                                  )}
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Role Label</label>
                                <input
                                  type="text"
                                  value={level.roleLabel}
                                  onChange={(e) => updateApprovalLevel(idx, 'roleLabel', e.target.value)}
                                  placeholder="e.g., Accounts Reviewer"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex justify-between pt-4">
                      <button
                        type="button"
                        onClick={() => setActiveSection('details')}
                        className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveSection('checklist')}
                        className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium flex items-center gap-2 shadow-md"
                      >
                        Next: Checklist
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Checklist Section - FIXED: Now only updates checklist without refreshing other sections */}
                {activeSection === 'checklist' && (
                  <div className="space-y-5 animate-in fade-in duration-300">
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Checklist Items</label>
                        <span className="text-xs text-gray-400">
                          {(formData.checklist || []).filter(i => i.trim()).length} items
                        </span>
                      </div>
                      <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                        {(formData.checklist || []).map((item, index) => (
                          <div key={index} className="flex gap-2 items-center group">
                            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                              {index + 1}
                            </div>
                            <input
                              type="text"
                              value={item}
                              onChange={(e) => handleChecklistChange(index, e.target.value)}
                              placeholder={`Task item ${index + 1}`}
                              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            />
                            {(formData.checklist || []).length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeChecklistItem(index)}
                                className="opacity-0 group-hover:opacity-100 p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={addChecklistItem}
                        className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Checklist Item
                      </button>
                    </div>

                    {/* Summary Card */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                      <h4 className="font-semibold text-gray-800 text-sm mb-2">📋 Task Summary</h4>
                      <div className="space-y-1 text-xs text-gray-600">
                        <p><strong>Title:</strong> {formData.title || 'Not set'}</p>
                        <p><strong>Preparors:</strong> {
                          selectedPreparors.filter(p => p).length > 0 
                            ? selectedPreparors.filter(p => p).map(p => getUserName(p)).join(', ')
                            : 'Not set'
                        }</p>
                        <p><strong>Approval Levels:</strong> {formData.approvalChain?.length || 0}</p>
                        <p><strong>Checklist Items:</strong> {(formData.checklist || []).filter(i => i.trim()).length}</p>
                      </div>
                    </div>

                    <div className="flex justify-between pt-4">
                      <button
                        type="button"
                        onClick={() => setActiveSection('approval')}
                        className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back
                      </button>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={handleClose}
                          className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={loading || !isBasicValid || selectedPreparors.length === 0 || selectedPreparors.every(p => !p)}
                          className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2 shadow-md"
                        >
                          {loading ? (
                            <>
                              <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Creating...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Create Task
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Create Task Type Modal */}
      {showCreateTaskTypeModal && (
        <>
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] transition-opacity duration-300"
            onClick={() => setShowCreateTaskTypeModal(false)}
          />
          <div className="fixed inset-0 z-[60] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div 
                className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-300"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-md">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l5 5a2 2 0 01.586 1.414V19a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">Create Task Type</h3>
                    </div>
                    <button
                      onClick={() => setShowCreateTaskTypeModal(false)}
                      className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Type Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newTaskTypeName}
                        onChange={(e) => setNewTaskTypeName(e.target.value)}
                        placeholder="e.g., Documentation, Research, Testing"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        autoFocus
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleCreateTaskType();
                          }
                        }}
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowCreateTaskTypeModal(false)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleCreateTaskType}
                        disabled={creatingTaskType || !newTaskTypeName.trim()}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2 shadow-md"
                      >
                        {creatingTaskType ? (
                          <>
                            <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Creating...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Create Type
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes zoomIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-in {
          animation-duration: 0.3s;
          animation-fill-mode: both;
        }
        .fade-in {
          animation-name: fadeIn;
        }
        .zoom-in {
          animation-name: zoomIn;
        }
      `}</style>
    </>
  );
}

export default TaskCreate;
